import * as React from 'react';
import { styled } from '@mui/material/styles';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { ActionOption, MoreActionsMenu } from '../MoreActionsMenu';
import { InfoTooltip } from '../InfoTooltip';

export interface UniversalCardProps {
    header?: string;
    subcontent?: React.ReactNode;
    children: React.ReactNode | React.ReactNode[];
    headerIcon?: React.ReactNode | React.ReactNode[];
    cardSx?: object;
    onClick?: () => void;
    showSubcontent?: boolean;
    showOptional?: boolean;
    showViewAll?: boolean;
    showInfoToolTip?: boolean;
    Infomsg?: string;
    // eslint-disable-next-line no-unused-vars
    onClickViewAll?: (flag: boolean) => void;
    actionMenuOptions?: ActionOption[];
    showErrorBorder?: boolean;
}

export const UniversalCard = ({
    header,
    children,
    subcontent,
    cardSx,
    headerIcon,
    // onClick,
    showSubcontent,
    showOptional,
    showViewAll,
    onClickViewAll,
    actionMenuOptions,
    showInfoToolTip,
    Infomsg,
    showErrorBorder
}: UniversalCardProps) => {
    const { t } = useTranslation();

    const StyledCard = styled('div')(({ theme }) => ({
        margin: 0,
        padding: '12px 24px',
        background: theme.palette.semantic.bg.default,
        borderRadius: '12px',
        border: `1px solid ${showErrorBorder ? theme.palette.semantic.bd.error : theme.palette.semantic.bd.default}`,
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
        ...cardSx
    }));

    const handleViewAll = () => {
        if (onClickViewAll) onClickViewAll(true);
    };

    const cardHeader = (
        <StyledHeaderDiv style={{ display: 'flex' }}>
            <StyledHeading>
                {header}
                {showOptional && <span>(Optional)</span>}
            </StyledHeading>
            {showInfoToolTip ? <InfoTooltip width={312} placement="right-start" t={Infomsg} /> : null}
        </StyledHeaderDiv>
    );

    return (
        <StyledCard>
            {header && (
                <>
                    <StyledCardHeader
                        avatar={headerIcon}
                        action={actionMenuOptions ? <MoreActionsMenu options={actionMenuOptions} /> : undefined}
                        title={cardHeader}
                    ></StyledCardHeader>
                    <StyledDivider />
                </>
            )}
            {(showSubcontent || showViewAll) && (
                <StyledSubHeaderSection>
                    {showSubcontent && <StyledSubcontent>{subcontent}</StyledSubcontent>}
                    {showViewAll && (
                        <StyledheaderViewAll onClick={handleViewAll}>
                            {t('VIEW_ALL_FUNDING_SOURCES')}
                        </StyledheaderViewAll>
                    )}
                </StyledSubHeaderSection>
            )}
            <StyledCardContent>{children}</StyledCardContent>
        </StyledCard>
    );
};

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    padding: '12px 0',
    '& .MuiCardHeader-root': {
        '& .MuiCardHeader-avatar': {
            marginRight: '12px'
        },
        '& .MuiSvgIcon-root': {
            fontWeight: '900'
        }
    },
    '& .MuiSvgIcon-root': {
        fill: theme.palette.neutral[400]
    }
}));

const StyledHeading = styled('h4')(({ theme }: any) => ({
    ...theme.typography.headers.h4,
    color: theme.palette.semantic.text.default,
    margin: 0,

    '> span': {
        color: theme.palette.neutral[300]
    }
}));

const StyledSubcontent = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.semantic.text.muted,
    margin: 0
}));

const StyledheaderViewAll = styled('span')(({ theme }) => ({
    fontSize: '13px',
    fontWeight: 600,
    fontStyle: 'normal',
    color: theme.palette.semantic.int.link,
    paddingLeft: 5,
    cursor: 'pointer',
    lineHeight: '18px',
    margin: 0,
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: 'TT Commons Pro',
    textDecorationLine: 'underline',
    textUnderlineOffset: '3px'
}));

const StyledHeaderDiv = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

const StyledDivider = styled(Divider)(({ theme }) => ({
    borderTop: '1px',
    marginBottom: '10px',
    borderColor: theme.palette.neutral[100]
}));

const StyledCardContent = styled(CardContent)({
    padding: 0,
    '&:last-child': {
        paddingBottom: 0
    }
});

const StyledSubHeaderSection = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    marginBottom: '10px'
});
